import { useCallback, useMemo, useRef, useState } from "react";
import DropdownList from "@atoms/inputs/Dropdown/components/DropdownList";
import useDropdownListHeight from "@atoms/inputs/Dropdown/hooks/useDropdownListHeight";
import useSetActiveSelect from "@atoms/inputs/Dropdown/hooks/useSetActiveSelect";
import useSetDropdownOpen from "@atoms/inputs/Dropdown/hooks/useSetDropdownOpen";
import InputError from "@atoms/inputs/InputError/InputError";
import { REMOVE_FOCUS_CSS } from "@components/Button/utils/constants";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import { cn } from "@util/helpers";

/** Input field - dropdown */
const Dropdown = ({
  options,
  placeholder,
  name,
  isDisplaySearch = false,
  variantClassName,
  onHandleChange,
  errorMessage,
}: DropdownFields) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(
    null
  );
  const childrenRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const itemElsRef = useRef<Array<HTMLLIElement | null>>([]);

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return options;

    return options.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const listboxId = `${name}-dropdown-listbox`;
  const optionIdBase = `${name}-dropdown-option`;

  const handleSelectOption = (option: DropdownOption) => {
    setSelectedOption(option);
    closeDropdown();
    setSearchQuery("");

    buttonRef.current?.blur();
    searchInputRef.current?.blur();

    if (!onHandleChange) return;

    onHandleChange(option.value);
  };

  const { isOpen, setIsOpen } = useSetDropdownOpen({
    containerRef,
  });

  const { activeIndex, handleSetActiveIndex, handleKeyDown } =
    useSetActiveSelect({
      isOpen,
      setIsOpen,
      filteredOptions,
      handleSelectOption,
      buttonRef,
      itemEls: itemElsRef.current,
    });

  const { childrenHeight } = useDropdownListHeight({
    childrenRef,
    isOpen,
  });

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    handleSetActiveIndex(-1);
  }, [handleSetActiveIndex, setIsOpen]);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    handleSetActiveIndex(-1);
  };

  const setItemEl = (index: number, el: HTMLLIElement | null) => {
    itemElsRef.current[index] = el;
  };

  const className = cn(variantClassName?.input, {
    [variantClassName?.focus || "border-gray-850"]: !errorMessage && isOpen,
    [variantClassName?.errorBorder || "border-red"]: errorMessage,
  });

  return (
    <div
      data-testid="dropdown-component"
      className={cn("w-full", variantClassName?.container)}
    >
      <div
        className={cn(
          "group relative block w-full text-left transition-all duration-300",
          className
        )}
        ref={containerRef}
      >
        <input type="hidden" value={selectedOption?.value ?? ""} name={name} />

        <button
          className={cn(
            "flex w-full items-center justify-between p-4 text-left",
            REMOVE_FOCUS_CSS
          )}
          type="button"
          ref={buttonRef}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          onClick={() => {
            toggleDropdown();
          }}
        >
          <p
            className={cn("mb-0 line-clamp-1 text-gray-850", {
              [variantClassName?.placeholder || ""]: !selectedOption?.label,
            })}
          >
            {selectedOption?.label || placeholder}{" "}
          </p>

          <IconFactory
            type={IconTypeEnum.chevronDown}
            className={cn("text-gray-550 transition-transform duration-300", {
              "-rotate-180": isOpen,
            })}
          />
        </button>

        <div
          className={cn(
            "absolute left-0 top-[calc(100%+1px)] z-[1000] h-0 w-full overflow-hidden rounded-3xl bg-white shadow-dropdown transition-all duration-300"
          )}
          ref={childrenRef}
          style={{ height: isOpen ? childrenHeight : 0 }}
        >
          {isDisplaySearch ? (
            <input
              type="text"
              placeholder={"Search"}
              className={cn(
                "w-full border-b border-gray-175 p-4 placeholder:font-poppins placeholder:text-sm placeholder:text-gray-550",
                REMOVE_FOCUS_CSS
              )}
              name="search"
              onKeyDown={handleKeyDown}
              onChange={handleSearchChange}
              value={searchQuery}
              ref={searchInputRef}
            />
          ) : null}

          {isDisplaySearch &&
          searchQuery.length > 0 &&
          filteredOptions.length === 0 ? (
            <div className="flex h-[calc(250px-56px)] items-center justify-center font-poppins text-sm leading-[150%] tracking-[1%] text-gray-550">
              No results found
            </div>
          ) : null}

          <DropdownList
            isDisplaySearch={isDisplaySearch}
            filteredOptions={filteredOptions}
            handleSelectOption={handleSelectOption}
            setItemEl={setItemEl}
            activeIndex={activeIndex}
            id={listboxId}
            optionIdBase={optionIdBase}
            variantClassName={variantClassName?.dropdownList || {}}
            selectedValue={selectedOption?.value}
          />
        </div>
      </div>

      {errorMessage ? <InputError error={errorMessage} /> : null}
    </div>
  );
};

export default Dropdown;
